// Licensed to the .NET Foundation under one or more agreements.
// The .NET Foundation licenses this file to you under the MIT license.

import { html, render } from 'lit-html'

/**
 * Language metadata from languages.json manifest
 */
export interface LanguageInfo {
  code: string
  name: string
  nativeName: string
  default?: boolean
  rtl?: boolean
}

/**
 * Languages manifest structure
 */
export interface LanguagesManifest {
  languages: LanguageInfo[]
  defaultLanguage: string
  generated: string
}

// Cache for the languages manifest
let manifestCache: LanguagesManifest | null = null

// Cache for UI strings
let uiStringsCache: Record<string, string> | null = null

/**
 * Fetches UI strings for the current language
 * Falls back to English if translation not available
 */
export async function fetchUiStrings(): Promise<Record<string, string>> {
  if (uiStringsCache) {
    return uiStringsCache
  }

  const currentLang = detectCurrentLanguage()

  try {
    // Try to fetch localized strings first
    let response = await fetch(`/${currentLang}/_ui-strings.json`)

    // Fall back to English if not found
    if (!response.ok && currentLang !== 'en') {
      response = await fetch('/en/_ui-strings.json')
    }

    if (!response.ok) {
      console.warn('UI strings not found')
      return getDefaultUiStrings()
    }

    uiStringsCache = await response.json()
    return uiStringsCache!
  } catch (error) {
    console.warn('Failed to load UI strings:', error)
    return getDefaultUiStrings()
  }
}

/**
 * Default UI strings fallback
 */
function getDefaultUiStrings(): Record<string, string> {
  return {
    aiTranslationWarning: 'This content was translated using AI and has not been reviewed by a human editor. Images and charts remain in their original language.',
    'header.nav.pricing': 'Pricing',
    'header.nav.download': 'Download',
    'header.nav.learn': 'Learn',
    'header.nav.resources': 'Resources',
    'header.nav.blog': 'Blog',
    'header.nav.newsletter': 'Newsletter',
    'header.nav.publications': 'Publications',
    'header.nav.documentation': 'Documentation',
    'header.nav.supportCommunity': 'Support community',
    'header.nav.contactUs': 'Contact Us',
    'header.button1': 'Free trial',
    'header.button2': 'Main page',
    'footer.heading': 'Ready to get started?',
    'footer.button1': 'Try Tabular Editor 3',
    'footer.button2': 'Buy Tabular Editor 3',
    'footer.aboutUs': 'About us',
    'footer.career': 'Career',
    'footer.newsroom': 'Newsroom',
    'footer.contactUs': 'Contact us',
    'footer.technicalSupport': 'Technical Support',
    'footer.securityTrust': 'Security & Trust Center',
    'footer.privacyPolicy': 'Privacy policy',
    'footer.cookiePolicy': 'Cookie policy',
    'footer.siteTerms': 'Site Terms',
    'footer.commercialTerms': 'Commercial Terms & Conditions',
    appliesTo: 'Applies to: ',
    availableSince: 'Available since',
    availableIn: 'Available in',
    inThisArticle: 'In this article',
    searchResultsCount: '{count} results for "{query}"',
    searchNoResults: 'No results for "{query}"',
    tocFilter: 'Filter by title',
    nextArticle: 'Next',
    prevArticle: 'Previous',
    themeLight: 'Light',
    themeDark: 'Dark',
    themeAuto: 'Auto',
    changeTheme: 'Change theme',
    copy: 'Copy',
    downloadPdf: 'Download PDF',
    search: 'Search documentation',
    note: 'Note',
    warning: 'Warning',
    tip: 'Tip',
    important: 'Important',
    caution: 'Caution',
    tableOfContents: 'Table of Contents',
    selectLanguage: 'Select language',
    copyCode: 'Copy code'
  }
}

/**
 * Fetches the languages manifest from the site root
 */
export async function fetchLanguagesManifest(): Promise<LanguagesManifest | null> {
  if (manifestCache) {
    return manifestCache
  }

  try {
    // Try to fetch from site root
    const response = await fetch('/languages.json')
    if (!response.ok) {
      console.warn('Languages manifest not found at /languages.json')
      return null
    }

    manifestCache = await response.json()
    return manifestCache
  } catch (error) {
    console.warn('Failed to load languages manifest:', error)
    return null
  }
}

/**
 * Detects the current language from the URL path
 * Expected URL pattern: /{lang}/path/to/page.html
 */
export function detectCurrentLanguage(): string {
  const pathSegments = window.location.pathname.split('/').filter(Boolean)

  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0].toLowerCase()
    // Check if first segment looks like a language code (2-5 chars, alphabetic)
    if (firstSegment.length >= 2 && firstSegment.length <= 5 && /^[a-z-]+$/.test(firstSegment)) {
      return firstSegment
    }
  }

  // Default fallback
  return 'en'
}

/**
 * Generates the URL for switching to a different language
 */
export function getLanguageSwitchUrl(targetLang: string): string {
  const currentPath = window.location.pathname
  const currentHash = window.location.hash
  const currentSearch = window.location.search

  // Replace the language segment in the path
  const pathSegments = currentPath.split('/').filter(Boolean)

  if (pathSegments.length > 0) {
    const firstSegment = pathSegments[0].toLowerCase()
    // Check if first segment is a language code
    if (firstSegment.length >= 2 && firstSegment.length <= 5 && /^[a-z-]+$/.test(firstSegment)) {
      pathSegments[0] = targetLang
    } else {
      // No language in path, prepend it
      pathSegments.unshift(targetLang)
    }
  } else {
    pathSegments.push(targetLang)
  }

  let newPath = '/' + pathSegments.join('/')

  // Preserve trailing slash from original path, or add one if path doesn't end with .html
  // This ensures relative asset paths resolve correctly
  if (currentPath.endsWith('/')) {
    newPath += '/'
  } else if (!newPath.endsWith('.html') && !newPath.includes('.')) {
    // Path doesn't have a file extension - add trailing slash for directory
    newPath += '/'
  }

  return newPath + currentSearch + currentHash
}

/**
 * Renders the language picker dropdown
 */
export async function renderLanguagePicker(): Promise<void> {
  const container = document.getElementById('language-picker')
  if (!container) {
    return
  }

  const manifest = await fetchLanguagesManifest()
  if (!manifest || manifest.languages.length <= 1) {
    // Hide picker if only one language or no manifest
    container.style.display = 'none'
    return
  }

  const currentLang = detectCurrentLanguage()
  const currentLangInfo = manifest.languages.find(l => l.code === currentLang) ||
    manifest.languages.find(l => l.default) ||
    manifest.languages[0]

  const uiStrings = await fetchUiStrings()
  const selectLanguageLabel = uiStrings.selectLanguage || 'Select language'

  const handleLanguageChange = (e: Event, lang: LanguageInfo) => {
    e.preventDefault()
    if (lang.code !== currentLang) {
      window.location.href = getLanguageSwitchUrl(lang.code)
    }
  }

  const template = html`
    <div class="language-picker dropdown">
      <button
        class="language-picker__toggle btn border-0 dropdown-toggle"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label="${selectLanguageLabel}"
        title="${selectLanguageLabel}">
        <i class="bi bi-globe"></i>
        <span class="language-picker__current">${currentLangInfo.nativeName}</span>
      </button>
      <ul class="language-picker__menu dropdown-menu dropdown-menu-end">
        ${manifest.languages.map(lang => html`
          <li>
            <a 
              class="dropdown-item ${lang.code === currentLang ? 'active' : ''}" 
              href="${getLanguageSwitchUrl(lang.code)}"
              @click=${(e: Event) => handleLanguageChange(e, lang)}
              lang="${lang.code}"
              ${lang.rtl ? html`dir="rtl"` : ''}>
              <span class="language-picker__name">${lang.nativeName}</span>
              ${lang.code !== currentLang ? html`<span class="language-picker__name-en">${lang.name}</span>` : ''}
            </a>
          </li>
        `)}
      </ul>
    </div>
  `

  render(template, container)
}

/**
 * Initialize the language picker
 */
export async function initLanguagePicker(): Promise<void> {
  await renderLanguagePicker()
}

/**
 * Applies UI string translations to all elements marked with data-ui-string attributes
 * and overrides DocFX loc meta tags with translated values from _ui-strings.json.
 * Runs for all languages, using _ui-strings.json as the single source of truth.
 */
export async function applyUiStringTranslations(): Promise<void> {
  const uiStrings = await fetchUiStrings()

  document.querySelectorAll<HTMLElement>('[data-ui-string]').forEach(el => {
    const key = el.getAttribute('data-ui-string')
    if (key && uiStrings[key] !== undefined) {
      el.textContent = uiStrings[key]
    }
  })

  // Override DocFX loc:* meta tags so loc() picks up translated strings
  document.querySelectorAll<HTMLMetaElement>('meta[name^="loc:"]').forEach(meta => {
    const key = meta.name.replace('loc:', '')
    if (uiStrings[key] !== undefined) {
      meta.content = uiStrings[key]
    }
  })

  // Override the search input placeholder and aria-label
  if (uiStrings.search) {
    const searchInput = document.getElementById('search-query') as HTMLInputElement
    if (searchInput) {
      searchInput.placeholder = uiStrings.search
      searchInput.setAttribute('aria-label', uiStrings.search)
    }
    const searchLabel = document.getElementById('header-search__form-label')
    if (searchLabel) {
      searchLabel.textContent = uiStrings.search
    }
  }

  // Override alert heading text (Note, Warning, Tip, Important, Caution)
  // DocFX renders alerts as <div class="NOTE alert alert-info"><h5>Note</h5>...
  // The original class (NOTE, TIP, etc.) is preserved on the element
  const alertClassMap: Record<string, string> = {
    NOTE: 'note',
    TIP: 'tip',
    WARNING: 'warning',
    IMPORTANT: 'important',
    CAUTION: 'caution'
  }
  for (const [cssClass, uiKey] of Object.entries(alertClassMap)) {
    if (uiStrings[uiKey]) {
      document.querySelectorAll<HTMLElement>(`.${cssClass} > h5`).forEach(h5 => {
        h5.textContent = uiStrings[uiKey]
      })
    }
  }
}

/**
 * Renders an AI translation warning banner for non-English content
 */
export async function renderTranslationWarning(): Promise<void> {
  const currentLang = detectCurrentLanguage()

  // Only show warning for non-English languages
  if (currentLang === 'en') {
    return
  }

  // Check if warning already exists
  if (document.getElementById('ai-translation-warning')) {
    return
  }

  // Fetch localized UI strings
  const uiStrings = await fetchUiStrings()

  const warningBanner = document.createElement('div')
  warningBanner.id = 'ai-translation-warning'
  warningBanner.className = 'ai-translation-warning'
  warningBanner.setAttribute('role', 'alert')

  warningBanner.innerHTML = `
    <div class="ai-translation-warning__content container-xxl">
      <i class="bi bi-info-circle ai-translation-warning__icon"></i>
      <span class="ai-translation-warning__text">
        ${uiStrings.aiTranslationWarning}
      </span>
    </div>
  `

  // Insert inside the header (at the end) so it scrolls with the header
  const header = document.querySelector('header.global-header_wrp')
  if (header) {
    header.appendChild(warningBanner)
  } else {
    // Fallback to body
    document.body.insertBefore(warningBanner, document.body.firstChild)
  }

  // Set CSS variable for banner height so sticky sidebars can account for it
  // Include extra 5px gap for visual separation
  requestAnimationFrame(() => {
    const bannerHeight = warningBanner.offsetHeight
    document.documentElement.style.setProperty('--ai-banner-height', `${bannerHeight + 5}px`)
  })
}
